import React from "react";
import { Box, Typography, Grid, Button, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { Chunk, Contact, useDealContext, Vessel } from "@/contexts/DealContext";
import { useNavigate, useParams } from "react-router-dom";
import BankingSyndicatesSummaryGrid from "@/components/review/BankingSyndicatesSummaryGrid";
import VesselReview from "@/components/review/VesselsReview";
import ContactReview from "@/components/review/ContactsReview";
import { capitalizeFirstLetter } from "@/components/review/LoanSetupStep";
import { iframeService } from "@/services/iframe.service";
import PreviewDisplay from "@/components/ui/PreviewDisplay";

const SummaryPage: React.FC = () => {
  const { deal, updateLoan } = useDealContext();
  const navigate = useNavigate();
  const { loanId } = useParams();
  const loan = deal.loans.find((l) => l.id === loanId) || deal.loans[0];
  const loanIndex = deal.loans.findIndex((l) => l.id === loanId);
  const isLastLoan = loanIndex === deal.loans.length - 1;
  const nextPage = isLastLoan ? "/summary" : `/review/loan/${deal.loans[loanIndex + 1].id}`;

  const next = () => {
    if (isLastLoan) {
      iframeService.sendMessage("end");
      return;
    }
    navigate(nextPage);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Loan Summary
        <IconButton onClick={() => navigate(`/review/loan/${loan.id}`)} size="small">
          <EditIcon fontSize="small" />
        </IconButton>
      </Typography>

      <Box>
        {/* Section: General Information */}
        <Box sx={{ m: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            General Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <PreviewDisplay title="Loan description">{loan.name}</PreviewDisplay>
            </Grid>

            {loan.type && (
              <Grid item xs={12} sm={6}>
                <PreviewDisplay title="Loan type">
                  {capitalizeFirstLetter(loan.type?.replaceAll("_", " "))}
                </PreviewDisplay>
              </Grid>
            )}

            {loan.maturity_date && (
              <Grid item xs={12} sm={6}>
                <PreviewDisplay title="Maturity date">
                  {loan.maturity_date.value.maturity_date
                    ? new Date(loan.maturity_date.value.maturity_date)?.toLocaleDateString(
                        "en-EN",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        }
                      )
                    : "-"}
                </PreviewDisplay>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <PreviewDisplay title="Amount">
                {Number(loan?.amount?.amount.value).toLocaleString("fi-FI", {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                  style: "currency",
                  currency: loan.amount?.currency?.value || "EUR",
                  currencyDisplay: "code",
                })}
              </PreviewDisplay>
            </Grid>

            {loan.agent && (
              <Grid item xs={12} sm={6}>
                <PreviewDisplay
                  title="Loan agent"
                  type="string"
                  value={loan.agent.value?.organization?.name as any}
                />
              </Grid>
            )}

            {loan.borrower && (
              <Grid item xs={12} sm={6}>
                <PreviewDisplay title="Borrower" type="string" value={loan.borrower.value as any} />
              </Grid>
            )}
            {loan.margin && (
              <Grid item xs={12} sm={6}>
                <PreviewDisplay title="Margin" type="string" value={loan.margin.value as any} />
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Section: Vessel Information */}
        {loan.vessels && loan.vessels.value.length > 0 && (
          <Box sx={{ m: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Asset
            </Typography>
            <VesselReview
              vessels={loan.vessels.value}
              onChange={(updatedVessels: Vessel[]) =>
                updateLoan(loan.id, {
                  vessels: {
                    value: updatedVessels,
                    chunk: loan.vessels?.chunk ? (loan.vessels.chunk as Chunk) : { chunk: "" },
                    validated: true,
                  },
                })
              }
            />
          </Box>
        )}

        {loan.banking_syndicate && Array.isArray(loan.banking_syndicate.value) && (
          <Box sx={{ m: 2, mb: 4 }}>
            <Typography variant="subtitle1" gutterBottom>
              Banking Syndicate
            </Typography>
            <BankingSyndicatesSummaryGrid syndicates={loan.banking_syndicate.value} loan={loan} />
          </Box>
        )}
        {loan.contacts && loan.contacts.value.length > 0 && (
          <Box sx={{ m: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Contacts
            </Typography>
            <ContactReview
              Contacts={loan.contacts.value}
              onChange={(updatedContacts: Contact[]) =>
                updateLoan(loan.id, {
                  contacts: {
                    value: updatedContacts,
                    chunk: loan.contacts?.chunk ? (loan.contacts.chunk as Chunk) : { chunk: "" },
                    validated: true,
                  },
                })
              }
            />
          </Box>
        )}
      </Box>

      <Box display="flex" justifyContent="space-between" mt={2}>
        <div style={{ flex: 1 }}>
          <Button variant="outlined" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
        <div style={{ gap: "10px", display: "flex" }}>
          {nextPage === "/summary" && (
            <Button variant="contained" color="primary" onClick={() => navigate("/upload")}>
              Add another loan
            </Button>
          )}
          <Button variant="contained" color="success" onClick={() => next()}>
            Confirm & Continue
          </Button>
        </div>
      </Box>
    </Box>
  );
};

export default SummaryPage;
